/**
 * In-browser video recording – camera preview, record/stop, returns recorded file.
 * Uses getUserMedia + MediaRecorder. Optimized for mobile (Safari/iOS, Chrome Android).
 */
import { useState, useRef, useEffect } from 'react';
import styles from './VideoRecorder.module.css';

/** Pick best MIME type for recording; Safari/iOS often needs mp4. */
function getSupportedMimeType() {
  const types = [
    'video/mp4',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return '';
}

function getExtension(mimeType) {
  if (mimeType && mimeType.indexOf('mp4') !== -1) return 'mp4';
  return 'webm';
}

export function VideoRecorder({ onRecorded, onError }) {
  const [status, setStatus] = useState('idle'); // idle | asking | live | recording | done
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedMimeType, setRecordedMimeType] = useState('');
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const videoPlaybackRef = useRef(null);

  async function startCamera() {
    setStatus('asking');
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported in this browser');
      }
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 640, max: 1280 },
          height: { ideal: 480, max: 720 },
        },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
      }
      setStatus('live');
    } catch (err) {
      onError?.(err.message || 'Camera access denied');
      setStatus('idle');
    }
  }

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoPreviewRef.current) videoPreviewRef.current.srcObject = null;
    setStatus('idle');
    setRecordedBlob(null);
  }

  function startRecording() {
    if (!streamRef.current) return;
    const mimeType = getSupportedMimeType();
    const options = mimeType ? { mimeType } : {};
    if (!mimeType || mimeType.indexOf('webm') !== -1) {
      options.videoBitsPerSecond = 2500000;
    }
    const recorder = new MediaRecorder(streamRef.current, options);
    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = () => {
      const type = recorder.mimeType || 'video/webm';
      const blob = new Blob(chunks, { type });
      setRecordedMimeType(type);
      setRecordedBlob(blob);
      setStatus('done');
      if (videoPlaybackRef.current) {
        videoPlaybackRef.current.src = URL.createObjectURL(blob);
      }
    };
    recorder.onerror = (e) => {
      onError?.(e.error?.message || 'Recording failed');
    };
    recorder.start(1000);
    recorderRef.current = recorder;
    setStatus('recording');
  }

  function stopRecording() {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
      recorderRef.current = null;
    }
  }

  function confirmRecorded() {
    if (recordedBlob) {
      const ext = getExtension(recordedMimeType || recordedBlob.type);
      const file = new File([recordedBlob], `recorded_${Date.now()}.${ext}`, { type: recordedBlob.type });
      onRecorded?.(file);
    }
    stopCamera();
  }

  function discardRecording() {
    setRecordedBlob(null);
    setStatus('live');
    if (videoPlaybackRef.current) {
      URL.revokeObjectURL(videoPlaybackRef.current.src);
      videoPlaybackRef.current.src = '';
    }
  }

  useEffect(() => {
    return () => {
      stopCamera();
      if (videoPlaybackRef.current?.src) URL.revokeObjectURL(videoPlaybackRef.current.src);
    };
  }, []);

  if (status === 'idle') {
    const hasMediaRecorder = typeof MediaRecorder !== 'undefined';
    return (
      <div className={styles.wrap}>
        {!hasMediaRecorder && (
          <p className={styles.message}>Video recording is not supported in this browser. Try Chrome or Safari (iOS 14.3+).</p>
        )}
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={startCamera}
          disabled={!hasMediaRecorder}
        >
          Record video
        </button>
      </div>
    );
  }

  if (status === 'asking') {
    return (
      <div className={styles.wrap}>
        <p className={styles.message}>Requesting camera access…</p>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      {/* Live preview (when not showing playback) */}
      {status !== 'done' && (
        <div className={styles.previewWrap}>
          <video
            ref={videoPreviewRef}
            className={styles.video}
            autoPlay
            muted
            playsInline
            aria-label="Camera preview"
          />
          {status === 'recording' && <span className={styles.recBadge}>Recording</span>}
        </div>
      )}

      {/* Playback of recorded video */}
      {status === 'done' && (
        <div className={styles.previewWrap}>
          <video ref={videoPlaybackRef} className={styles.video} controls playsInline aria-label="Recorded video" />
        </div>
      )}

      <div className={styles.actions}>
        {status === 'live' && (
          <button type="button" className={styles.recordBtn} onClick={startRecording}>
            Start recording
          </button>
        )}
        {status === 'recording' && (
          <button type="button" className={styles.stopBtn} onClick={stopRecording}>
            Stop recording
          </button>
        )}
        {status === 'done' && (
          <>
            <button type="button" className={styles.discardBtn} onClick={discardRecording}>
              Record again
            </button>
            <button type="button" className={styles.confirmBtn} onClick={confirmRecorded}>
              Use this video
            </button>
          </>
        )}
        <button type="button" className={styles.cancelBtn} onClick={stopCamera}>
          Cancel
        </button>
      </div>
    </div>
  );
}
